import React, { memo, useContext } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useLocation } from 'react-router-dom';
import { UIDReset } from 'react-uid';
import queryString from 'querystring';

import '@prisjakt/ui-base/main.scss';
import { noop } from '@prisjakt/ui-base';
import { ViewportTracker } from '@prisjakt/ui-viewport';
import '@prisjakt/ui-base/styles/normalize.css';
import ExperimentEditor from '@prisjakt/ui-experiment/src/editor';
import { AppContext } from '@prisjakt/ui-app-context';
import Spinner from '@prisjakt/ui-spinner';

import MainHeader from './areas/header';
import Legacy from './areas/legacy';
import Footer from './areas/footer';
import CookieBanner from './components/cookie-banner';
import ErrorMessage from './components/error-message';
import ErrorBoundary from './components/error-boundary';
import ToastContainer from './components/toast-container';
import Sidebar from './areas/sidebar';
import Viewport from './components/viewport';
import FetchContext from './components/fetch-context';
import StaticIntercom from './components/static-intercom';
import NewsletterSignupModal, {
  NEWSLETTER_PARAM,
} from './components/newsletter-signup-modal';

import Feedback from './areas/feedback';
import { usePrefetchAllLoadOrders } from './hooks/experiments';

import TransitionRouter from './routes';
import TocModal from './components/toc-modal';
import addPerformanceTiming from './client/performance-reporter';

import './components/toast-container/sagas/toast';

import routeConfig from './routes/config';

// Types
import { AppState } from './reducers';
import { User } from './types';

import * as S from './styles';

type Error = {
  message: string;
  stack: string;
};

const Error500 = () => <ErrorMessage status={500} />;
const Error500WithMessage = (error: Error) => (
  <ErrorMessage message={error.message} stack={error.stack} status={500} />
);

/**
 * A number so that we can track the render after the
 * initial mounting. This is when experiments are loaded and viewportcontext is updated.
 * This is used to add user performance timings.
 */
let renders = 0;

type Props = {
  infoPages: number[];
  campaigns: AppState['globals']['campaigns'];
  me: User;
  user?: UserSettings;
  layout?: string;
  isAuthenticated: boolean;
  renderToastContainer?: boolean;
  renderRoutes: boolean;
};

type UserSettings = {
  settings: {
    behavior_tracking_improvement: boolean; // eslint-disable-line camelcase
    behavior_tracking_marketing: boolean; // eslint-disable-line camelcase
    behavior_tracking_surveys: boolean; // eslint-disable-line camelcase
    personalized_ads: boolean; // eslint-disable-line camelcase
    personalization: boolean;
  };
  personalization: {
    clientId: string;
  };
};

const App = ({
  layout = 'default',
  campaigns,
  me,
  user,
  isAuthenticated,
  renderToastContainer,
  renderRoutes,
}: Props) => {
  const prefetchAllLoadOrders = usePrefetchAllLoadOrders();
  const location = useLocation();

  const {
    market: { routes, code },
    renderMode,
  } = useContext(AppContext);

  renders += 1;
  if (renders === 2) {
    addPerformanceTiming('firstRerender');
  }

  const legacyMode = ['desktop', 'mobile', 'native'].includes(renderMode);

  const router = renderRoutes ? (
    <FetchContext.Consumer>
      {(fetchContext: any) => {
        return (
          <TransitionRouter
            loaderAfter={1500}
            prefetchAllLoadOrders={prefetchAllLoadOrders}
            fetchContext={fetchContext}
            campaigns={campaigns}
            routes={routes}
            personalizationClientId={
              user && user.personalization && user.personalization.clientId
            }
            isAuthenticated={isAuthenticated}
            routeConfig={routeConfig}
          />
        );
      }}
    </FetchContext.Consumer>
  ) : (
    <Spinner size={32} isActive />
  );

  if (legacyMode) {
    return (
      <ErrorBoundary onError={Error500}>
        <ViewportTracker>
          <Legacy router={router} />
        </ViewportTracker>
      </ErrorBoundary>
    );
  }

  const isFrontPage = ['/', ''].includes(location.pathname);
  const openNewsletterSignup = Object.keys(
    queryString.parse(location.search.substr(1))
  ).includes(NEWSLETTER_PARAM);

  return (
    <ErrorBoundary onError={Error500}>
      <UIDReset>
        <S.App layout={layout}>
          {openNewsletterSignup && <NewsletterSignupModal />}

          <Viewport tablet mobile>
            <ExperimentEditor />
          </Viewport>

          <ViewportTracker>
            <MainHeader />
            {renderToastContainer && <ToastContainer />}
            <Viewport desktop tablet>
              <Feedback />
            </Viewport>
            <ErrorBoundary onError={noop}>
              <CookieBanner />
            </ErrorBoundary>
            <ErrorBoundary onError={noop}>
              <StaticIntercom />
            </ErrorBoundary>
            <ErrorBoundary onError={Error500WithMessage}>
              <S.Main data-test="AppMain" data-test-market-id={code}>
                <Sidebar isFrontPage={isFrontPage} />

                <S.Content>
                  {router}
                  <Footer />
                </S.Content>
              </S.Main>
            </ErrorBoundary>
            {me && me.updatedTerms && me.updatedTerms.length > 0 && (
              <TocModal items={me.updatedTerms} />
            )}
          </ViewportTracker>
        </S.App>
      </UIDReset>
    </ErrorBoundary>
  );
};

const mapStateToProps = (state: AppState) => ({
  ...state.globals,
  me: state.areas.account.me,
  user: state.areas.user,
  isAuthenticated: state.areas.account.isAuthenticated,
  renderToastContainer: state.toast.required,
});

export default compose(connect(mapStateToProps), memo)(App);
